import axios from 'axios';
import { getActiveStoreContext } from '../lib/storefront/storeBootstrap.js';
import { getGoogleOAuthReturnInfo } from '../lib/storefront/storePath.js';
import { isAdminDashboardToken } from '../utils/authToken.js';

// Base URL for all API calls
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token + active store to requests
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token && !isAdminDashboardToken(token)) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        const storeId = getActiveStoreContext()?.store?.id;
        if (storeId) {
            config.headers['X-Store-Id'] = storeId;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const token = localStorage.getItem('token');
            if (token && !isAdminDashboardToken(token)) {
                localStorage.removeItem('token');
                localStorage.removeItem('userId');
                window.dispatchEvent(new Event('app:auth-cleared'));
            }
        }
        return Promise.reject(error);
    }
);

// ============================================
// Authentication API
// ============================================

export const authAPI = {
    // User login
    login: (credentials) => {
        return apiClient.post('/api/appUser/login', credentials);
    },

    // User registration
    register: (userData) => {
        return apiClient.post('/api/appUser/register', userData);
    },

    // Forgot password
    forgotPassword: (data) => {
        return apiClient.post('/api/appUser/forgot-password', data);
    },

    // Reset password
    resetPassword: (data) => {
        return apiClient.patch('/api/appUser/reset-password', data);
    },

    // Get user data
    getUserData: () => {
        return apiClient.get('/api/appUser/user');
    },

    // Google-only customers (no password yet): email a one-time code, then set the first password
    requestPasswordSetupCode: () => {
        return apiClient.post('/api/appUser/password/setup-code');
    },

    setupPassword: ({ code, newPassword, confirmPassword }) => {
        return apiClient.post('/api/appUser/password/setup', { code, newPassword, confirmPassword });
    },

    // Update user profile
    updateProfile: (userId, profileData) => {
        return apiClient.put(`/api/appUser/users/${userId}`, profileData);
    },

    // Update user profile avatar
    updateAvatar: (userId, file) => {
        const formData = new FormData();
        formData.append('avatar', file);
        return apiClient.put(`/api/appUser/users/${userId}/avatar`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    // Logout
    logout: () => {
        return apiClient.post('/api/appUser/logout');
    },

    // Google OAuth — full-page redirect cannot send X-Store-Id; pass store + return URLs in query.
    // intent: 'login' only signs in existing customers; 'register' only creates new ones.
    googleAuth: ({ intent = 'login' } = {}) => {
        const storeId = getActiveStoreContext()?.store?.id;
        if (!storeId) {
            throw new Error('Store is not loaded yet. Please refresh the page and try again.');
        }
        const { returnOrigin, returnPath, signInPath, signUpPath } = getGoogleOAuthReturnInfo();
        const params = new URLSearchParams({
            storeId,
            returnOrigin,
            returnPath,
            signInPath,
            signUpPath,
            intent,
        });
        return `${API_BASE_URL}/api/appUser/google?${params.toString()}`;
    },
};
